import { serviceName } from '../resources/serviceConstants';
import {
  PROJECT_MANAGEMENT_PROJECT_DETAILS,
  PROJECT_MANAGEMENT_PROJECT_TYPE,
  CHANGE_REQUEST,
  RESOURCE_ALLOCATION_DEPARTMENTS,
} from '../resources/endPoint';

const PROJECT_DETAILS_BASE = PROJECT_MANAGEMENT_PROJECT_DETAILS;

const buildProjectRequest = (url, method = 'get', payload, token = '') => ({
  url,
  method,
  headers: {
    'Content-Type': 'application/json',
    ...(token ? { token, isAuthToken: true } : {}),
  },
  authRequired: false,
  csrfToken: true,
  ...(payload !== undefined ? { payload } : {}),
  service: serviceName.PROJECT_MS,
  timeout: 180000,
});

const appendQueryParams = (baseUrl, query = {}, cacheBust = '') => {
  const searchParams = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });

  if (cacheBust) {
    searchParams.append('cb', cacheBust);
  }

  const queryString = searchParams.toString();
  return `${baseUrl}${queryString ? `?${queryString}` : ''}`;
};

export const getProjectDetailsList = () => buildProjectRequest(PROJECT_DETAILS_BASE);

export const getProjectDetailsBySeqId = (projectSeqId, cacheBust = '') => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/${projectSeqId}${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getProjectDetailsByProjectId = (projectId, cacheBust = '') => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/by-project-id/${projectId}${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getProjectDetailsComplete = (projectSeqId, cacheBust = '') => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/${projectSeqId}/complete${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

const getProjectDtlCompleteByProjectId = (projectId, cacheBust = '') => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/by-project-id/${projectId}/complete${
    cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''
  }`,
);

export { getProjectDtlCompleteByProjectId as getProjectDetailsCompleteByProjectId };

export const getProjectDetailsCustomers = (cacheBust = '') => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/customers${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getPrimaryReasons = (cacheBust = '') => buildProjectRequest(
  `/v1/primary-reasons${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getSecondaryReasons = (cacheBust = '') => buildProjectRequest(
  `/v1/secondary-reasons${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getReasonReports = (query = {}, cacheBust = '') => buildProjectRequest(
  appendQueryParams('/v1/reason-report', query, cacheBust),
);

export const createReasonReport = (payload) => buildProjectRequest(
  '/v1/reason-report',
  'post',
  payload,
);

export const updateReasonReport = (reportId, payload) => buildProjectRequest(
  `/v1/reason-report/${encodeURIComponent(String(reportId || ''))}`,
  'put',
  payload,
);

export const getEffortEstimations = (query = {}, cacheBust = '') => buildProjectRequest(
  appendQueryParams('/v1/effort-estimation', query, cacheBust),
);

export const getResourceTypes = (query = {}, cacheBust = '') => buildProjectRequest(
  appendQueryParams('/v1/resource-type', query, cacheBust),
);

export const getRoleExperienceMappings = (cacheBust = '') => buildProjectRequest(
  `/v1/resource-type/experience-mappings${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getResourceCategories = (query = {}, cacheBust = '') => buildProjectRequest(
  appendQueryParams('/v1/resource-category', query, cacheBust),
);

export const getEmployeeMasterList = (cacheBust = '') => buildProjectRequest(
  `/v1/employee-master${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getEstimatedGmByProjectId = (projectId) => buildProjectRequest(
  `/v1/estimated-gm${projectId ? `?projectId=${projectId}` : ''}`,
);

export const getGrossMarginDataByProjectId = (projectId) => buildProjectRequest(
  `/v1/gross-margin-data${projectId ? `?projectId=${projectId}` : ''}`,
);

export const getProjectProgressByProjectId = (projectId, cacheBust = '') => buildProjectRequest(
  `/v1/project-progress${projectId || cacheBust ? `?${[
    projectId ? `projectId=${encodeURIComponent(projectId)}` : '',
    cacheBust ? `cb=${encodeURIComponent(cacheBust)}` : '',
  ].filter(Boolean).join('&')}` : ''}`,
);

export const getProjectUnits = (query = {}, cacheBust = '') => buildProjectRequest(
  appendQueryParams('/v1/projectUnit', query, cacheBust),
);

export const createProjectUnit = (payload) => buildProjectRequest(
  '/v1/projectUnit',
  'post',
  payload,
);

export const updateProjectUnit = (unitId, payload) => buildProjectRequest(
  `/v1/projectUnit/${encodeURIComponent(String(unitId || ''))}`,
  'put',
  payload,
);

export const deleteProjectUnit = (unitId) => buildProjectRequest(
  `/v1/projectUnit/${encodeURIComponent(String(unitId || ''))}`,
  'delete',
);

export const getProjectUnitResourceCategories = (cacheBust = '') => buildProjectRequest(
  `${CHANGE_REQUEST}/edit-form-options/resource-category${cacheBust ? `?cb=${encodeURIComponent(cacheBust)}` : ''}`,
);

export const getCsatCustomerDetailsByProjectId = (projectId, cacheBust = '') => buildProjectRequest(
  `/v1/csat-customer-details${projectId || cacheBust ? `?${[
    projectId ? `projectId=${encodeURIComponent(projectId)}` : '',
    cacheBust ? `cb=${encodeURIComponent(cacheBust)}` : '',
  ].filter(Boolean).join('&')}` : ''}`,
);

export const createCsatCustomerDetails = (payload) => buildProjectRequest(
  '/v1/csat-customer-details',
  'post',
  payload,
);

export const updateCsatCustomerDetails = (customerDetailsId, payload) => buildProjectRequest(
  `/v1/csat-customer-details/${customerDetailsId}`,
  'put',
  payload,
);

export const deleteCsatCustomerDetails = (customerDetailsId) => buildProjectRequest(
  `/v1/csat-customer-details/${customerDetailsId}`,
  'delete',
);


export const createProjectCsat = (payload) => buildProjectRequest(
  '/v1/project-csat',
  'post',
  payload,
);

export const updateProjectCsat = (csatId, payload) => buildProjectRequest(
  `/v1/project-csat/${csatId}`,
  'put',
  payload,
);

export const deleteProjectCsat = (csatId, payload) => buildProjectRequest(
  `/v1/project-csat/${csatId}`,
  'delete',
  payload,
);

export const getCsatHistoryRecords = (query = {}, cacheBust = '') => buildProjectRequest(
  appendQueryParams('/v1/csat-history', query, cacheBust),
);

export const createProjectProgress = (payload, token = '') => buildProjectRequest(
  '/v1/project-progress',
  'post',
  payload,
  token,
);

export const updateProjectProgress = (progressId, payload, token = '') => buildProjectRequest(
  `/v1/project-progress/${progressId}`,
  'put',
  payload,
  token,
);

export const deleteProjectProgress = (progressId, token = '') => buildProjectRequest(
  `/v1/project-progress/${progressId}`,
  'delete',
  undefined,
  token,
);

export const getProjectStatusMaster = (projectSeqId) => buildProjectRequest(
  projectSeqId
    ? `${PROJECT_DETAILS_BASE}/project-statuses?projectSeqId=${encodeURIComponent(projectSeqId)}`
    : `${PROJECT_DETAILS_BASE}/project-statuses`,
);

export const getProjectTypeMaster = () => buildProjectRequest(
  PROJECT_MANAGEMENT_PROJECT_TYPE,
);

export const updateProjectDetails = (projectSeqId, payload) => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/${projectSeqId}`,
  'put',
  payload,
);

export const deleteProjectDetails = (projectSeqId, payload) => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/${projectSeqId}`,
  'delete',
  payload,
);

export const restoreProjectDetails = (projectSeqId, payload) => buildProjectRequest(
  `${PROJECT_DETAILS_BASE}/${projectSeqId}/restore`,
  'put',
  payload,
);

export const getProjectDetailsMilestone = (query = {}, cacheBust = '') => {
  const searchParams = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });

  if (cacheBust) {
    searchParams.append('cb', cacheBust);
  }

  const queryString = searchParams.toString();

  return {
    url: `/v1/project-details-milestone${queryString ? `?${queryString}` : ''}`,
    method: 'get',
    headers: { 'Content-Type': 'application/json' },
    authRequired: false,
    csrfToken: true,
    service: serviceName.PROJECT_MS,
    timeout: 180000,
  };
};

export const createResourceAllocation = (payload) => buildProjectRequest(
  '/v1/resource-allocation',
  'post',
  payload,
);

export const updateResourceAllocation = (allocationId, payload) => buildProjectRequest(
  `/v1/resource-allocation/${allocationId}`,
  'put',
  payload,
);

export const deleteResourceAllocation = (allocationId) => buildProjectRequest(
  `/v1/resource-allocation/${allocationId}`,
  'delete',
);

export const getScrums = () => buildProjectRequest(
  '/v1/resource-allocation/scrums',
  'get',
);

export const getStaffingRequests = (projectId) => buildProjectRequest(
  `/v1/staffing-request/dropdown?project_id=${encodeURIComponent(projectId)}`,
  'get',
);

export const createTbhResourceAllocation = (payload) => buildProjectRequest(
  '/v1/tbh-resource-allocation',
  'post',
  payload,
);

export const updateTbhResourceAllocation = (tbhId, payload) => buildProjectRequest(
  `/v1/tbh-resource-allocation/${tbhId}`,
  'put',
  payload,
);

export const deleteTbhResourceAllocation = (tbhId) => buildProjectRequest(
  `/v1/tbh-resource-allocation/${tbhId}`,
  'delete',
);

export const getTbhEmployees = () => buildProjectRequest(
  '/v1/tbh-employees',
  'get',
);

export const getEmployeeExperiences = () => buildProjectRequest(
  '/v1/employee-experiences',
  'get',
);

export const getLocationTypes = () => buildProjectRequest(
  '/v1/location-types',
  'get',
);

export const getDepartmentsList = () => buildProjectRequest(
  RESOURCE_ALLOCATION_DEPARTMENTS,
  'get',
);

export const updateBulkTbhResourceAllocation = (payload) => buildProjectRequest(
  '/v1/tbh-resource-allocation/bulk-update',
  'put',
  payload,
);
